import React, { useState } from "react";
import { Link } from "react-router-dom";
import { dummyAnnouncements } from "../../../data/dummyData";
import "../Home/Homepage.css";
import "./AnnouncementManagementPage.css";

const CATEGORY_OPTIONS = ["General", "Trading", "Security", "Launchpad", "Maintenance"];

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const AnnouncementManagementPage = () => {
  const [activeView, setActiveView] = useState("create"); // create | list
  const [announcements, setAnnouncements] = useState(dummyAnnouncements);
  const [form, setForm] = useState({ category: "", title: "", description: "" });

  const counts = {
    total: announcements.length,
    active: announcements.filter((a) => (a.status || "").toLowerCase() === "active").length,
    inactive: announcements.filter((a) => (a.status || "").toLowerCase() === "inactive").length,
  };

  const updateForm = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handlePublish = (e) => {
    e.preventDefault();
    const newAnn = {
      _id: "ann_" + Date.now(),
      category: form.category,
      title: form.title,
      description: form.description,
      createdAt: new Date().toISOString(),
      status: "Active",
    };
    setAnnouncements((prev) => [newAnn, ...prev]);
    setForm({ category: "", title: "", description: "" });
  };

  const setStatus = (id, status) => {
    setAnnouncements((prev) => prev.map((a) => (a._id === id ? { ...a, status } : a)));
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this announcement?")) {
      setAnnouncements((prev) => prev.filter((a) => a._id !== id));
    }
  };

  return (
    <main className="dashboard-main dashboard-subpage announcement-page">
      <section className="dashboard-hero announcement-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Announcement</span>
          </p>
          <h1 className="dashboard-hero__title">Announcement Management</h1>
          <p className="dashboard-hero__subtitle">Create and manage platform announcements</p>
        </div>
        <div className="announcement-hero__icon">
          <i className="fas fa-bullhorn" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="announcement-cards">
          <div className="announcement-card">
            <span className="announcement-card__value">{counts.total}</span>
            <span className="announcement-card__label">Total Announcements</span>
          </div>
          <div className="announcement-card announcement-card--active">
            <span className="announcement-card__value">{counts.active}</span>
            <span className="announcement-card__label">Active</span>
          </div>
          <div className="announcement-card announcement-card--inactive">
            <span className="announcement-card__value">{counts.inactive}</span>
            <span className="announcement-card__label">Inactive</span>
          </div>
        </div>

        <div className="announcement-tabs">
          <button
            type="button"
            className={`announcement-tab announcement-tab--create ${activeView === "create" ? "announcement-tab--active" : ""}`}
            onClick={() => setActiveView("create")}
          >
            <i className="fas fa-plus" /> Create Announcement
          </button>
          <button
            type="button"
            className={`announcement-tab announcement-tab--list ${activeView === "list" ? "announcement-tab--active" : ""}`}
            onClick={() => setActiveView("list")}
          >
            <i className="fas fa-list" /> Announcement List
          </button>
        </div>

        <div className="announcement-content">
          {activeView === "create" && (
            <section className="announcement-section">
              <h2 className="announcement-section__title"><i className="fas fa-pen" /> Create New Announcement</h2>
              <form className="announcement-form" onSubmit={handlePublish}>
                <div className="announcement-form__group">
                  <label>Announcement Category <span className="required">*</span></label>
                  <select value={form.category} onChange={(e) => updateForm("category", e.target.value)} required>
                    <option value="">Select Category</option>
                    {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="announcement-form__group">
                  <label>Announcement Title <span className="required">*</span></label>
                  <input type="text" placeholder="Enter Announcement Title" value={form.title} onChange={(e) => updateForm("title", e.target.value)} required />
                </div>
                <div className="announcement-form__group">
                  <label>Description <span className="required">*</span></label>
                  <textarea placeholder="Write your description..." value={form.description} onChange={(e) => updateForm("description", e.target.value)} rows={6} required />
                </div>
                <div className="announcement-form__actions">
                  <button type="submit" className="announcement-btn announcement-btn--publish">
                    <i className="fas fa-paper-plane" /> Publish Announcement
                  </button>
                </div>
              </form>
            </section>
          )}

          {activeView === "list" && (
            <section className="announcement-section">
              <h2 className="announcement-section__title"><i className="fas fa-list" /> Announcement List</h2>
              <div className="announcement-table-wrap">
                <table className="announcement-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Created</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {announcements.map((row) => (
                      <tr key={row._id}>
                        <td><strong>{row.title}</strong></td>
                        <td>{row.category}</td>
                        <td className="announcement-table__desc">{row.description}</td>
                        <td>{formatDate(row.createdAt)}</td>
                        <td><span className={`announcement-status announcement-status--${(row.status || "").toLowerCase()}`}>{row.status}</span></td>
                        <td>
                          <div className="announcement-actions">
                            <button type="button" className="announcement-action-btn announcement-action-btn--active" onClick={() => setStatus(row._id, "Active")} title="Set Active">
                              Active
                            </button>
                            <button type="button" className="announcement-action-btn announcement-action-btn--inactive" onClick={() => setStatus(row._id, "Inactive")} title="Set Inactive">
                              Inactive
                            </button>
                            <button type="button" className="announcement-action-btn announcement-action-btn--delete" onClick={() => handleDelete(row._id)} title="Delete">
                              <i className="fas fa-trash-alt" /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      </div>
    </main>
  );
};

export default AnnouncementManagementPage;
